import React from 'react';
import { Box, Typography, Container, Grid, Card, CardContent, CardMedia, Button } from '@mui/material';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Briefcase, Users, Target, Rocket, ChevronRight } from 'lucide-react';

const INTERNSHIP_APPLY_FORM_URL = 'https://forms.gle/LSRsjxA49uHjU8Tt5';

const INTERNSHIPS = [
  {
    id: 'agentic-ai',
    title: 'Agentic AI Intern',
    subtitle: 'LangChain & Chatbot Development',
    shortDesc: 'Build intelligent AI systems using LLMs and automation frameworks.',
    image: '/ai-for-everyone.jpg',
    duration: '2–6 Months',
    skills: ['Python', 'LangChain', 'LLMs', 'RAG', 'FastAPI'],
  },
  {
    id: 'physical-ai',
    title: 'Physical AI Intern',
    subtitle: 'Robotics & Python AI',
    shortDesc: 'Build intelligent physical systems combining robotics and AI.',
    image: '/beyond/gallery/gallery-1.jpg',
    duration: '2–6 Months',
    skills: ['Python', 'Robotics', 'IoT', 'OpenCV', 'Arduino'],
  },
];

const InternshipHubPage: React.FC = () => {
  const navigate = useNavigate();

  return (
    <Box sx={{ minHeight: '100vh', pt: { xs: 8, md: 10 }, pb: 8, bgcolor: '#F8FAFC' }}>
      {/* Hero Section */}
      <Box
        sx={{
          position: 'relative',
          color: '#fff',
          py: { xs: 6, md: 8 },
          px: 2,
          mb: 6,
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            inset: 0,
            backgroundImage: 'url(/beyond/live-events.jpg)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            opacity: 0.2,
          },
          '&::after': {
            content: '""',
            position: 'absolute',
            inset: 0,
            background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 60%, #0f172a 100%)',
          },
        }}
      >
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
              <Briefcase size={24} color="#60a5fa" />
              <Typography sx={{ color: '#93c5fd', fontWeight: 600, fontSize: '0.9rem', textTransform: 'uppercase', letterSpacing: 1 }}>
                Internship Hub
              </Typography>
            </Box>
            <Typography component="h1" sx={{ fontSize: { xs: '2rem', md: '2.75rem' }, fontWeight: 800, mb: 2, lineHeight: 1.2 }}>
              About Our Internship Program
            </Typography>
            <Typography sx={{ fontSize: { xs: '1rem', md: '1.15rem' }, color: 'rgba(255,255,255,0.9)', lineHeight: 1.8, maxWidth: 800 }}>
              At Internship Hub, we provide hands-on, industry-focused internships designed to transform students into job-ready professionals. Our programs are built around real-world projects, mentorship from industry experts, and cutting-edge technologies.
            </Typography>
            <Typography sx={{ fontSize: { xs: '1rem', md: '1.1rem' }, color: 'rgba(255,255,255,0.95)', lineHeight: 1.8, mt: 2, maxWidth: 800 }}>
              Whether you&apos;re passionate about Artificial Intelligence, Robotics, or Software Development, we give you the platform to learn, build, and innovate.
            </Typography>
          </motion.div>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ px: 2 }}>
        {/* Open Positions Section */}
        <Box sx={{ mb: 6 }}>
          <Typography component="h2" sx={{ fontSize: { xs: '1.5rem', md: '1.75rem' }, fontWeight: 700, color: '#1e293b', mb: 3 }}>
            Open Internship Positions
          </Typography>

          <Grid container spacing={4}>
            {INTERNSHIPS.map((intern, i) => (
              <Grid size={{ xs: 12, md: 6 }} key={intern.id}>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: i * 0.1 }}
                >
                  <Card
                    onClick={() => navigate(`/internship-hub/${intern.id}`)}
                    sx={{
                      height: '100%',
                      cursor: 'pointer',
                      overflow: 'hidden',
                      borderRadius: '16px',
                      border: '1px solid #e5e7eb',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-6px)',
                        boxShadow: '0 20px 40px rgba(37, 99, 235, 0.15)',
                        borderColor: '#2563EB',
                      },
                    }}
                  >
                    <Box sx={{ position: 'relative', height: 200, overflow: 'hidden' }}>
                      <CardMedia
                        component="img"
                        image={intern.image}
                        alt={intern.title}
                        sx={{
                          height: '100%',
                          objectFit: 'cover',
                          transition: 'transform 0.4s ease',
                          '&:hover': { transform: 'scale(1.05)' },
                        }}
                      />
                      <Box
                        sx={{
                          position: 'absolute',
                          inset: 0,
                          background: 'linear-gradient(to top, rgba(0,0,0,0.7), transparent 50%)',
                          display: 'flex',
                          alignItems: 'flex-end',
                          p: 2,
                        }}
                      >
                        <Box>
                          <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '1.25rem' }}>{intern.title}</Typography>
                          <Typography sx={{ color: 'rgba(255,255,255,0.9)', fontSize: '0.9rem' }}>{intern.subtitle}</Typography>
                        </Box>
                      </Box>
                    </Box>
                    <CardContent sx={{ p: 3 }}>
                      <Typography sx={{ color: '#64748b', mb: 2, lineHeight: 1.6 }}>{intern.shortDesc}</Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                        {intern.skills.map((skill) => (
                          <Typography
                            key={skill}
                            sx={{
                              fontSize: '0.75rem',
                              fontWeight: 600,
                              color: '#2563EB',
                              bgcolor: '#EAF2FF',
                              px: 1.5,
                              py: 0.5,
                              borderRadius: '8px',
                            }}
                          >
                            {skill}
                          </Typography>
                        ))}
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Typography sx={{ fontSize: '0.9rem', color: '#64748b', fontWeight: 500 }}>
                          {intern.duration}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                          <Button
                            component="a"
                            href={INTERNSHIP_APPLY_FORM_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            variant="contained"
                            size="small"
                            sx={{
                              bgcolor: '#2563EB',
                              color: '#fff',
                              fontWeight: 600,
                              textTransform: 'none',
                              px: 2,
                              py: 0.75,
                              '&:hover': { bgcolor: '#1D4ED8' },
                            }}
                            onClick={(e: React.MouseEvent) => e.stopPropagation()}
                          >
                            Apply
                          </Button>
                          <Button
                            endIcon={<ChevronRight size={18} />}
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/internship-hub/${intern.id}`);
                            }}
                            sx={{
                              color: '#2563EB',
                              fontWeight: 600,
                              textTransform: 'none',
                              '&:hover': { bgcolor: 'rgba(37, 99, 235, 0.08)' },
                            }}
                          >
                            View Details
                          </Button>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Internship Details Summary */}
        <Box sx={{ mb: 6 }}>
          <Grid container spacing={4}>
            <Grid size={{ xs: 12, md: 6 }}>
              <motion.div initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  <Box sx={{ bgcolor: '#EAF2FF', p: 1.5, borderRadius: '12px' }}>
                    <Target size={28} color="#2563EB" />
                  </Box>
                  <Typography component="h3" sx={{ fontSize: '1.25rem', fontWeight: 700, color: '#1e293b' }}>
                    What You Will Build
                  </Typography>
                </Box>
                <Box component="ul" sx={{ pl: 2.5, '& li': { color: '#64748b', mb: 1, lineHeight: 1.7 } }}>
                  <li>AI Chatbots & Virtual Assistants</li>
                  <li>RAG-based Intelligent Systems</li>
                  <li>Robotics Projects with AI Integration</li>
                  <li>Smart Automation Systems</li>
                  <li>Real-world Industry Use Cases</li>
                </Box>
              </motion.div>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <motion.div initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  <Box sx={{ bgcolor: '#ECFDF5', p: 1.5, borderRadius: '12px' }}>
                    <Rocket size={28} color="#10b981" />
                  </Box>
                  <Typography component="h3" sx={{ fontSize: '1.25rem', fontWeight: 700, color: '#1e293b' }}>
                    Internship Details
                  </Typography>
                </Box>
                <Box sx={{ display: 'grid', gap: 1.5 }}>
                  {[
                    { icon: '🕒', text: 'Duration: 2–6 Months' },
                    { icon: '📍', text: 'Mode: Online / Hybrid' },
                    { icon: '🎓', text: 'Eligibility: Students & Freshers (BCA, B.Tech, MCA, etc.)' },
                    { icon: '💰', text: 'Stipend: Performance-based' },
                    { icon: '📜', text: 'Certification: Yes' },
                  ].map((item) => (
                    <Box key={item.text} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Typography sx={{ fontSize: '1.2rem' }}>{item.icon}</Typography>
                      <Typography sx={{ color: '#64748b', fontWeight: 500 }}>{item.text}</Typography>
                    </Box>
                  ))}
                </Box>
              </motion.div>
            </Grid>
          </Grid>
        </Box>

        {/* Vision & CTA */}
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
          <Box
            sx={{
              background: 'linear-gradient(135deg, #2563EB 0%, #1D4ED8 100%)',
              borderRadius: '20px',
              p: { xs: 4, md: 6 },
              color: '#fff',
              textAlign: 'center',
            }}
          >
            <Users size={48} style={{ marginBottom: 16, opacity: 0.9 }} />
            <Typography component="h3" sx={{ fontSize: { xs: '1.25rem', md: '1.5rem' }, fontWeight: 700, mb: 2 }}>
              Our Vision
            </Typography>
            <Typography sx={{ fontSize: '1rem', opacity: 0.95, maxWidth: 700, mx: 'auto', mb: 4, lineHeight: 1.8 }}>
              We aim to build a strong ecosystem of AI innovators, developers, and problem solvers who can create impactful solutions for real-world challenges.
            </Typography>
            <Typography sx={{ fontSize: '1.1rem', fontWeight: 600, mb: 3 }}>Learn. Build. Innovate. 🚀</Typography>
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, justifyContent: 'center', alignItems: 'center', flexWrap: 'wrap' }}>
              <Button
                component="a"
                href={INTERNSHIP_APPLY_FORM_URL}
                target="_blank"
                rel="noopener noreferrer"
                variant="contained"
                sx={{
                  bgcolor: '#fff',
                  color: '#2563EB',
                  fontWeight: 700,
                  px: 4,
                  py: 1.5,
                  borderRadius: '12px',
                  '&:hover': { bgcolor: '#f1f5f9', color: '#1D4ED8' },
                }}
              >
                Apply Here
              </Button>
              <Button
                component="a"
                href="tel:+919716817710"
                variant="outlined"
                sx={{
                  borderColor: 'rgba(255,255,255,0.8)',
                  color: '#fff',
                  fontWeight: 600,
                  px: 4,
                  py: 1.5,
                  borderRadius: '12px',
                  '&:hover': { borderColor: '#fff', bgcolor: 'rgba(255,255,255,0.1)' },
                }}
              >
                📞 9716817710
              </Button>
            </Box>
            <Typography sx={{ mt: 2, fontSize: '0.9rem', opacity: 0.9 }}>
              Email: revivingindiaclub@gmail.com
            </Typography>
          </Box>
        </motion.div>
      </Container>
    </Box>
  );
};

export default InternshipHubPage;
